import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Video, Users, DollarSign, Mail, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-[#0A0A0A] p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider font-bold text-[#4B5563]">{label}</span>
      <Icon className="w-4 h-4 text-[#6D28B5]" />
    </div>
    <div className="text-3xl font-black tracking-tighter">{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter">Dashboard</h1>
          <p className="text-[#4B5563] mt-1">Your experience engine at a glance.</p>
        </div>
        <Link
          to="/admin/webinars"
          className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] px-5 py-2.5 font-semibold transition-colors flex items-center gap-2"
        >
          Manage experiences <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {!stats ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Video} label="Experiences" value={stats.total_webinars} testid="stat-webinars" />
            <Link to="/admin/webinars" className="block transition-transform hover:-translate-y-0.5" data-testid="stat-registrants-link">
              <StatCard icon={Users} label="Registrants" value={stats.total_registrants} testid="stat-registrants" />
            </Link>
            <Link to="/admin/transactions" className="block transition-transform hover:-translate-y-0.5" data-testid="stat-revenue-link">
              <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue}`} testid="stat-revenue" />
            </Link>
            <StatCard icon={Mail} label="Campaigns" value={stats.campaigns} testid="stat-campaigns" />
          </div>

          {isAdmin && (
            <>
              <h2 className="text-lg font-bold tracking-tight mb-4">Integrations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { key: "stripe", label: "Stripe Payments", note: "Test mode active" },
                  { key: "bunny", label: "Bunny.net Storage", note: "Image uploads" },
                  { key: "stream", label: "Bunny.net Stream", note: "Long video uploads" },
                  { key: "brevo", label: "Brevo Email", note: "Campaign sending" },
                ].map((it) => {
                  const ok = stats.integrations[it.key];
                  return (
                    <div key={it.key} className="bg-white border border-[#E5E7EB] p-5 flex items-center justify-between" data-testid={`integration-${it.key}`}>
                      <div>
                        <div className="font-semibold">{it.label}</div>
                        <div className="text-xs text-[#4B5563] mt-0.5">{it.note}</div>
                      </div>
                      {ok ? (
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-[#10B981]">
                          <CheckCircle2 className="w-4 h-4" /> Connected
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-[#9CA3AF]">
                          <XCircle className="w-4 h-4" /> Add keys
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
